import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Alert from "react-bootstrap/esm/Alert";
import Button from "react-bootstrap/esm/Button";
import Form from "react-bootstrap/esm/Form";
import Modal from "react-bootstrap/esm/Modal";
import Spinner from "react-bootstrap/esm/Spinner";
import ChoirSong from "../../DTOs/ChoirSong";
import { renameChoirSong } from "../../services/choirSongService";

interface SongMergeModalProps {
    gemeindeId: string;
    song: ChoirSong | null;
    songs: ChoirSong[];
    onClose: () => void;
    onMerged: (message: string) => void;
}

/** Merges a song into another one: its performances and service entries move there, the song disappears. */
export default function SongMergeModal({ gemeindeId, song, songs, onClose, onMerged }: SongMergeModalProps) {
    const { t } = useTranslation();
    const [targetId, setTargetId] = useState("");
    const [merging, setMerging] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        setTargetId("");
        setError("");
    }, [song]);

    const target = songs.find((candidate) => candidate.id === targetId);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!song || !target) return;
        setMerging(true);
        setError("");
        try {
            const result = await renameChoirSong(gemeindeId, song.id, target.name.toString(), true);
            onMerged(t("repertoire.merged", {
                from: song.name.toString(),
                to: result.name,
                moved: result.movedPerformances,
                services: result.updatedServices,
            }));
        } catch (err: any) {
            setError(err?.message || t("errors.anErrorOccurred"));
        } finally {
            setMerging(false);
        }
    };

    return (
        <Modal show={!!song} onHide={merging ? undefined : onClose} centered>
            <Form onSubmit={handleSubmit}>
                <Modal.Header closeButton={!merging}>
                    <Modal.Title>{t("repertoire.mergeTitle")}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {error && <Alert variant="danger">{error}</Alert>}
                    <p>{t("repertoire.mergeIntro", { name: song?.name.toString() ?? "" })}</p>
                    <Form.Group controlId="songMerge.target">
                        <Form.Label>{t("repertoire.mergeTarget")}</Form.Label>
                        <Form.Select required value={targetId} onChange={(e) => setTargetId(e.target.value)}>
                            <option value="">{t("repertoire.mergeChoose")}</option>
                            {songs.filter((candidate) => candidate.id !== song?.id).map((candidate) => (
                                <option key={candidate.id} value={candidate.id}>{candidate.name.toString()}</option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                    {target && (
                        <Alert variant="warning" className="mt-3 mb-0">
                            {t("repertoire.mergeWarning", { from: song?.name.toString() ?? "", to: target.name.toString() })}
                        </Alert>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary" onClick={onClose} disabled={merging}>{t("common.cancel")}</Button>
                    <Button type="submit" variant="danger" disabled={merging || !target}>
                        {merging && <Spinner animation="border" size="sm" className="me-2" />}
                        {t("repertoire.merge")}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
}
